import React, { useState, useEffect } from 'react';
import '../../styles/admin/AdminCompanyList.css';
import { useNavigate } from 'react-router-dom';
import { getAdminCompanyList, getCompanyPlanInfo, updateCompanyAccessStatus, getAdminMiddleUsers, getMiddleUserByCompanyCode, deleteCompany } from '../../utils/api';
import Dialog from '../../components/Dialog';

const AdminCompanyList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showOnlyAccessAllowed, setShowOnlyAccessAllowed] = useState(false);
  const [accessPermissions, setAccessPermissions] = useState({});
  const [selectedOption, setSelectedOption] = useState('Index');
  const [allCompanies, setAllCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planInfoLoading, setPlanInfoLoading] = useState(false);
  const [middleUsers, setMiddleUsers] = useState([]);
  const [isMiddleUsersLoading, setIsMiddleUsersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    // 회사 목록과 중간관리자 목록을 함께 가져오기
    const fetchData = async () => {
      try {
        setLoading(true);
        setIsMiddleUsersLoading(true);
        setError(null);
        
        // 중간관리자 목록 가져오기
        const middleUsersResponse = await getAdminMiddleUsers();
        console.log('중간관리자 목록 응답:', middleUsersResponse);
        
        if (middleUsersResponse && middleUsersResponse.middle_users) {
          setMiddleUsers(middleUsersResponse.middle_users);
          
          // 중간관리자 정보를 기반으로 회사 목록 생성
          const companiesFromMiddleUsers = middleUsersResponse.middle_users.map(user => {
            return {
              id: Math.random().toString(36).substr(2, 9), // 임의의 ID 생성
              name: user.middleCompanyName || '이름 없음',
              phone: user.middlePhone || '',
              businessNumber: user.middleBusinessNumber || '',
              companyCode: user.middleCompanyCode || '', // 비어있어도 포함
              expiryDate: '', // 플랜 정보는 별도로 가져올 예정
              accessAllowed: user.isAccessAllowed === undefined ? false : user.isAccessAllowed,
              middleUserId: user.middleUserID || null,
              // 추가 정보
              middleName: user.middleName || '',
              userInfo: user // 원본 중간관리자 정보 보존
            };
          });
          
          console.log('중간관리자 정보로 생성된 회사 목록:', companiesFromMiddleUsers);
          setAllCompanies(companiesFromMiddleUsers);
          setFilteredCompanies(companiesFromMiddleUsers);
          
          // 접속허용 상태 설정
          const initialAccessState = {};
          companiesFromMiddleUsers.forEach(company => {
            initialAccessState[company.id] = company.accessAllowed;
          });
          setAccessPermissions(initialAccessState);
          
          // 각 회사의 플랜 정보 불러오기
          fetchPlanInfo(companiesFromMiddleUsers);
        } else {
          throw new Error('중간관리자 데이터를 찾을 수 없습니다');
        }
        
      } catch (err) {
        console.error('데이터 로딩 오류:', err);
        
        // 예시 데이터 로드 (API 실패시)
        const dummyData = [
          { id: 1, name: '김밥천국 동대문점', phone: '010-1234-5678', businessNumber: '123-45-67890', companyCode: 'asf123', expiryDate: '2025-10-19', accessAllowed: true },
          { id: 2, name: '김밥천국 종로점', phone: '010-2345-6789', businessNumber: '234-56-78901', companyCode: 'asf456', expiryDate: '2025-08-15', accessAllowed: false },
          { id: 3, name: '김밥천국 강남점', phone: '010-3456-7890', businessNumber: '345-67-89012', companyCode: 'asf789', expiryDate: '2025-12-31', accessAllowed: true },
        ];
        
        setAllCompanies(dummyData);
        setFilteredCompanies(dummyData);
        
        // 초기 접속허용 상태 설정
        const initialAccessState = {};
        dummyData.forEach(company => {
          initialAccessState[company.id] = company.accessAllowed;
        });
        setAccessPermissions(initialAccessState);
        
        setError(`회사 목록을 불러오는 중 오류가 발생했습니다: ${err.message}`);
      } finally {
        setLoading(false);
        setIsMiddleUsersLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // 각 회사의 플랜 정보 가져오기
  const fetchPlanInfo = async (companies) => {
    try {
      setPlanInfoLoading(true);
      
      // 업데이트된 회사 데이터를 저장할 배열
      const updatedCompanies = [...companies];
      
      // 각 회사별로 플랜 정보 요청
      for (let i = 0; i < companies.length; i++) {
        const company = companies[i];
        if (company.companyCode) {
          try {
            const planInfo = await getCompanyPlanInfo(company.companyCode);
            
            if (planInfo && planInfo.status === 'success' && planInfo.data) {
              // 플랜 만료일 업데이트
              let formattedEndDate = '';
              if (planInfo.data.end_date) {
                // "2025-11-25T23:50:33.858000" 형식에서 "T" 앞부분만 추출
                formattedEndDate = planInfo.data.end_date.split('T')[0];
              }
              
              updatedCompanies[i] = {
                ...company,
                expiryDate: formattedEndDate || company.expiryDate
              };
              console.log(`${company.name}의 플랜 만료일 업데이트: ${formattedEndDate}`);
            }
          } catch (error) {
            console.warn(`${company.name}(${company.companyCode})의 플랜 정보 로드 실패:`, error);
          }
        }
      }
      
      // 업데이트된 회사 정보로 상태 갱신
      setAllCompanies(updatedCompanies);
      
      // 필터링된 회사 목록도 업데이트
      applyFilters(updatedCompanies);
      
    } catch (error) {
      console.error('플랜 정보 조회 중 오류:', error);
    } finally {
      setPlanInfoLoading(false);
    }
  };
  
  useEffect(() => {
    applyFilters();
  }, [showOnlyAccessAllowed, searchTerm, allCompanies, accessPermissions, selectedOption]);
  
  // 선택된 정렬 옵션에 따라 회사 목록을 정렬하는 함수
  const sortCompanies = (companies, sortOption) => {
    const sortedCompanies = [...companies];
    
    switch (sortOption) {
      case 'Index':
        // 기본 상태 유지 (이미 인덱스 순서대로 정렬됨)
        return sortedCompanies;
      
      case '업체명':
        return sortedCompanies.sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB, 'ko-KR');
        });
      
      case '전화번호':
        return sortedCompanies.sort((a, b) => {
          // 전화번호가 없는 경우 빈 문자열로 처리
          const phoneA = a.phone || '';
          const phoneB = b.phone || '';
          // 숫자만 추출하여 비교 (숫자 정렬)
          const numA = phoneA.replace(/\D/g, '');
          const numB = phoneB.replace(/\D/g, '');
          
          if (numA === '' && numB === '') return 0;
          if (numA === '') return 1;
          if (numB === '') return -1;
          
          return numA.localeCompare(numB, 'ko-KR', { numeric: true });
        });
      
      case '사업자번호':
        return sortedCompanies.sort((a, b) => {
          const bnA = a.businessNumber || '';
          const bnB = b.businessNumber || '';
          // 사업자번호도 숫자로 정렬 (하이픈 제거하고 숫자 비교)
          const numA = bnA.replace(/\D/g, '');
          const numB = bnB.replace(/\D/g, '');
          
          if (numA === '' && numB === '') return 0;
          if (numA === '') return 1;
          if (numB === '') return -1;
          
          return numA.localeCompare(numB, 'ko-KR', { numeric: true });
        });
      
      case '회사코드':
        return sortedCompanies.sort((a, b) => {
          const codeA = a.companyCode || '';
          const codeB = b.companyCode || '';
          // 대소문자 구분 없이 정렬하기 위해 소문자로 변환 후 비교
          return codeA.toLowerCase().localeCompare(codeB.toLowerCase(), 'ko-KR', { numeric: true, sensitivity: 'base' });
        });
      
      case '플랜 만료일':
        return sortedCompanies.sort((a, b) => {
          const dateA = a.expiryDate || '';
          const dateB = b.expiryDate || '';
          
          // 날짜가 없는 경우 맨 뒤로 정렬
          if (dateA === '' && dateB === '') return 0;
          if (dateA === '') return 1;
          if (dateB === '') return -1;
          
          // 날짜 문자열을 Date 객체로 변환하여 비교
          try {
            const dateObjA = new Date(dateA);
            const dateObjB = new Date(dateB);
            
            // 유효한 날짜인지 확인
            const isValidA = !isNaN(dateObjA.getTime());
            const isValidB = !isNaN(dateObjB.getTime());
            
            if (!isValidA && !isValidB) return 0;
            if (!isValidA) return 1;
            if (!isValidB) return -1;
            
            // 날짜 비교
            return dateObjA - dateObjB;
          } catch (e) {
            // 날짜 변환 오류 시 문자열 비교로 fallback
            return dateA.localeCompare(dateB, 'ko-KR');
          }
        });
      
      default:
        return sortedCompanies;
    }
  };
  
  const applyFilters = (companies = allCompanies) => {
    let filtered = [...companies];
    
    if (showOnlyAccessAllowed) {
      filtered = filtered.filter(company => accessPermissions[company.id]);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(company => 
        company.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // 정렬 옵션 적용
    filtered = sortCompanies(filtered, selectedOption);
    
    setFilteredCompanies(filtered);
  };
  
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  
  const toggleAccessFilter = () => {
    setShowOnlyAccessAllowed(!showOnlyAccessAllowed);
  };
  
  const handleAccessChange = async (id) => {
    // 현재 상태의 반대 값
    const newStatus = !accessPermissions[id];
    
    // 해당 회사 정보 찾기
    const company = allCompanies.find(c => c.id === id);
    if (!company || !company.companyCode) {
      alert('회사 코드를 찾을 수 없어 접속 허용 상태를 변경할 수 없습니다.');
      return;
    }
    
    try {
      // 상태 변경 중 UI에 표시
      setAccessPermissions(prev => ({
        ...prev,
        [id]: newStatus
      }));
      
      // 상태 변경 API 호출 - 중간관리자 ID 대신 회사 코드 사용
      await updateCompanyAccessStatus(company.companyCode, newStatus);
      
      console.log(`회사 ID ${id}의 접속 허용 상태가 ${newStatus ? '허용' : '차단'}으로 변경되었습니다.`);
    } catch (error) {
      // 오류 발생 시 원래 상태로 되돌림
      setAccessPermissions(prev => ({
        ...prev,
        [id]: !newStatus
      }));
      
      console.error('접속 허용 상태 변경 오류:', error);
      alert(`접속 허용 상태 변경 중 오류가 발생했습니다: ${error.message}`);
    }
  };
  
  const handleDelete = (id) => {
    // 삭제할 회사 정보 찾기
    const company = allCompanies.find(c => c.id === id);
    if (!company) return;

    // 선택된 회사 ID 저장
    setSelectedCompanyId(id);
    
    // 삭제 확인 다이얼로그 표시
    setDialogType('company-delete-confirm');
    setDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCompanyId) return;
    
    // 선택된 회사 정보 찾기
    const company = allCompanies.find(c => c.id === selectedCompanyId);
    if (!company) return;
    
    try {
      setIsDeleting(true);
      
      // 회사 코드와 사업자번호 가져오기
      const companyCode = company.companyCode;
      const businessNumber = company.businessNumber;
      
      console.log('회사 삭제 요청 중...', { companyCode, businessNumber });
      
      // 다이얼로그 닫기
      setDialogOpen(false);
      
      // API 호출 - company_code를 우선 사용하고, 없으면 business_number 사용
      const response = await deleteCompany(companyCode, businessNumber);
      console.log('회사 삭제 응답:', response);
      
      // 삭제 성공 시 회사 목록에서 제거
      const updatedCompanies = allCompanies.filter(c => c.id !== selectedCompanyId);
      setAllCompanies(updatedCompanies);
      
      // 필터링된 목록도 업데이트
      const updatedFilteredCompanies = filteredCompanies.filter(c => c.id !== selectedCompanyId);
      setFilteredCompanies(updatedFilteredCompanies);
      
      // 성공 다이얼로그 표시
      setTimeout(() => {
        setDialogType('company-delete-success');
        setDialogOpen(true);
      }, 300);
      
    } catch (error) {
      console.error('회사 삭제 오류:', error);
      
      // 오류 다이얼로그 표시
      setTimeout(() => {
        setDialogType('company-delete-error');
        setDialogOpen(true);
      }, 300);
    } finally {
      setIsDeleting(false);
      setSelectedCompanyId(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCompanyClick = async (id, companyCode, businessNumber) => {
    try {
      // 회사 코드와 사업자번호를 로컬 스토리지에 저장 (상세 페이지에서 사용)
      if (companyCode) {
        localStorage.setItem(`company_${id}_code`, companyCode);
      }
      if (businessNumber) {
        localStorage.setItem(`company_${id}_business_number`, businessNumber);
      }
      
      // 중간관리자 정보 조회 - 회사코드 또는 사업자번호 사용
      console.log(`회사 ID ${id} 정보 조회 중...`);
      console.log(`회사코드: ${companyCode || '없음'}, 사업자번호: ${businessNumber || '없음'}`);
      
      let middleUserData;
      try {
        // 회사코드가 있으면 회사코드로 조회, 없으면 사업자번호로 조회
        middleUserData = await getMiddleUserByCompanyCode(companyCode, businessNumber);
        
        // 응답 데이터 확인 및 콘솔에 출력
        if (middleUserData && middleUserData.middle_users && middleUserData.middle_users.length > 0) {
          console.log('조회된 중간관리자 정보:', middleUserData.middle_users);
          
          // 관련된 중간관리자 정보가 있는 경우 첫 번째 항목 출력
          const matchedUser = companyCode && companyCode.trim() !== '' 
            ? middleUserData.middle_users.find(user => user.middleCompanyCode === companyCode)
            : middleUserData.middle_users[0]; // 사업자번호로 검색한 경우 첫 번째 결과 사용
          
          if (matchedUser) {
            console.log('일치하는 중간관리자 세부 정보:', {
              아이디: matchedUser.middleUserID,
              회사명: matchedUser.middleCompanyName,
              담당자: matchedUser.middleName,
              전화번호: matchedUser.middlePhone,
              사업자번호: matchedUser.middleBusinessNumber,
              접속허용여부: matchedUser.isAccessAllowed
            });
          }
        } else {
          console.log(`해당 ${companyCode ? '회사코드' : '사업자번호'}로 일치하는 중간관리자 정보를 찾을 수 없습니다.`);
        }
      } catch (apiError) {
        console.error('중간관리자 정보 조회 API 오류:', apiError);
        console.log('API 오류가 발생했지만 페이지 이동은 계속 진행합니다.');
      }
      
      // 상세 페이지로 이동
      navigate(`/admin/company-detail/${id}`);
    } catch (error) {
      console.error('중간관리자 정보 조회 중 오류 발생:', error);
      // 오류가 발생해도 상세 페이지로 이동
      navigate(`/admin/company-detail/${id}`);
    }
  };

  const selectOption = (option) => {
    setSelectedOption(option);
    setIsDropdownOpen(false);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // 이전 페이지로 이동
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // 다음 페이지로 이동
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // 현재 페이지에 표시할 데이터 계산
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCompanies.slice(startIndex, endIndex);
  };
  
  // 총 페이지 수 계산
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  
  // 페이지 번호 배열 생성 (최대 10개 페이지 표시)
  const renderPageNumbers = () => {
    const pageNumbers = [];
    
    // 시작 페이지와 끝 페이지 계산
    let startPage = Math.max(1, currentPage - 4);
    let endPage = Math.min(startPage + 9, totalPages);
    
    if (endPage - startPage < 9) {
      startPage = Math.max(1, endPage - 9);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button 
          key={i} 
          className={`pageNumber ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    return pageNumbers;
  };

  return (
    <div className="adminCompanyListContainer" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: 'calc(100vh - 80px)'
    }}>
      <div className="contentArea">
        {/* 로딩 인디케이터 */}
        {(loading || planInfoLoading || isMiddleUsersLoading) && (
          <div className="loadingMessage" style={{ textAlign: 'center', padding: '20px' }}>
            {loading ? '회사 목록을 불러오는 중입니다...' : 
             isMiddleUsersLoading ? '중간관리자 정보를 불러오는 중입니다...' : 
             '회사 플랜 정보를 불러오는 중입니다...'}
          </div>
        )}
        
        {/* 에러 메시지 */}
        {error && (
          <div className="errorMessage" style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
            {error}
          </div>
        )}
        
        <div className="filterSection">
          <div className="filterLeft">
            <div className="filterItem">
              <label className="filterLabel">정렬</label>
              <div className="dropdown-container">
                <div className="dropdown-header" onClick={toggleDropdown}>
                  <span>{selectedOption}</span>
                  <span className={`arrow-icon ${isDropdownOpen ? 'up' : 'down'}`}>
                    &#9650;
                  </span>
                </div>
                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    <div 
                      className={`dropdown-item ${selectedOption === 'Index' ? 'selected' : ''}`}
                      onClick={() => selectOption('Index')}
                    >
                      Index
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '업체명' ? 'selected' : ''}`}
                      onClick={() => selectOption('업체명')}
                    >
                      업체명
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '전화번호' ? 'selected' : ''}`}
                      onClick={() => selectOption('전화번호')}
                    >
                      전화번호
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '사업자번호' ? 'selected' : ''}`}
                      onClick={() => selectOption('사업자번호')}
                    >
                      사업자번호
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '회사코드' ? 'selected' : ''}`}
                      onClick={() => selectOption('회사코드')}
                    >
                      회사코드
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '플랜 만료일' ? 'selected' : ''}`}
                      onClick={() => selectOption('플랜 만료일')}
                    >
                      플랜 만료일
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="filterRight">
            <div className="accessToggleContainer">
              <label className="accessToggleLabel">
                <span>접속허용 여부</span>
                <input 
                  type="checkbox" 
                  checked={showOnlyAccessAllowed} 
                  onChange={toggleAccessFilter}
                  className="accessToggleCheckbox"
                />
              </label>
            </div>
            
            <div className="filterItem">
              <label className="filterLabel">업체명 검색</label>
              <div className="searchWrapper" style={{ width: '215px' }}>
                <input
                  type="text"
                  className="searchInput"
                  style={{ height: '45px' }}
                  placeholder="업체명 입력"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="searchButton">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="black"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="totalRecords">총 {filteredCompanies.length}건</div>
        
        {/* 데이터가 없는 경우 메시지 표시 */}
        {!loading && filteredCompanies.length === 0 && (
          <div className="emptyMessage" style={{ textAlign: 'center', padding: '30px' }}>
            조회된 회사가 없습니다.
          </div>
        )}
        
        <div className="tableContainer">
          {!loading && filteredCompanies.length > 0 && (
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Index</th>
                  <th>업체명</th>
                  <th>전화번호</th>
                  <th>사업자번호</th>
                  <th>회사코드</th>
                  <th>플랜 만료일</th>
                  <th>접속허용</th>
                  <th>삭제</th>
                </tr>
              </thead>
              <tbody>
                {getCurrentPageData().map((company, index) => (
                  <tr key={company.id}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>
                      <span 
                        className="companyNameLink" 
                        onClick={() => handleCompanyClick(company.id, company.companyCode, company.businessNumber)}
                      >
                        {company.name}
                      </span>
                    </td>
                    <td>{company.phone}</td>
                    <td>{company.businessNumber}</td>
                    <td>{company.companyCode}</td>
                    <td>{company.expiryDate}</td>
                    <td className="accessColumn">
                      <input 
                        type="checkbox" 
                        checked={accessPermissions[company.id] || false} 
                        onChange={() => handleAccessChange(company.id)}
                        className="accessCheckbox"
                      />
                    </td>
                    <td className="deleteColumn">
                      <button 
                        className="deleteButton" 
                        onClick={() => handleDelete(company.id)}
                        aria-label="삭제"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#666"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      {/* 데이터가 있는 경우에만 페이지네이션 표시 */}
      {!loading && filteredCompanies.length > 0 && (
        <div className="paginationArea" style={{ marginTop: 'auto', paddingTop: '50px', paddingBottom: '30px' }}>
          <div className="pagination">
            <button 
              className="pageNav" 
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
              &lt;
            </button>
            {renderPageNumbers()}
            <button 
              className="pageNav" 
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
      
      {/* 다이얼로그 컴포넌트 추가 */}
      <Dialog 
        isOpen={dialogOpen} 
        type={dialogType} 
        onClose={handleDialogClose} 
        onConfirm={(e) => {
          if (dialogType === 'company-delete-confirm') {
            handleDeleteConfirm();
          } else {
            handleDialogClose();
          }
        }}
        onCancel={handleDialogClose}
      />
    </div>
  );
};

export default AdminCompanyList; 